import React, { ComponentProps, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Checkbox } from 'record-it-components/input/Checkbox/Checkbox';
import { Icon, IconType } from 'record-it-components/misc/Icon/Icon';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { classes } from 'record-it-components/utils/utils';
import { EntityName } from 'record-it-time-common/EntityName';
import { UserGroupToUser } from '../../../../classes/entities/UserGroupToUser/userGroupToUserTypes';
import { useEntity } from '../../../../hooks/entity/useEntity/useEntity';
import { TimeTrackingConfigurationSelect } from '../../../timeTrackingConfiguration/TimeTrackingConfigurationSelect/TimeTrackingConfigurationSelect';
import styles from './UserGroupToUsersWidgetUser.module.css';

export type UserGroupToUsersWidgetUserProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  userGroupId: string;
  userGroupToUser: UserGroupToUser;
  onDelete: (userGroupToUser: UserGroupToUser) => void;
  onUserGroupUserChanged: (userGroupToUser: UserGroupToUser) => void;
};

export const UserGroupToUsersWidgetUser: VoidFunctionComponent<UserGroupToUsersWidgetUserProps> =
  React.memo((props) => {
    const {
      userGroupId,
      userGroupToUser,
      onDelete,
      onUserGroupUserChanged,
      className,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const user = useEntity(EntityName.User, 'getById', userGroupToUser.userId);

    const handleDeleteButtonClick = useEventCallback(() => {
      onDelete(userGroupToUser);
    });

    const handleGroupAdminChanged = useEventCallback((groupAdmin: boolean) => {
      onUserGroupUserChanged({
        ...userGroupToUser,
        groupAdmin
      });
    });

    const handleTimeTrackingConfigurationIdChanged = useEventCallback(
      (timeTrackingConfigurationId: string | null) => {
        onUserGroupUserChanged({
          ...userGroupToUser,
          timeTrackingConfigurationId
        });
      }
    );

    return (
      <div
        {...remainingProps}
        className={classes(className, styles.user)}
      >
        <span data-testid="UserGroupToUsersWidgetUser.UserName">
          {user
            ? user.email
            : t(
                'components.userGroup.UserGroupToUsersWidget.UserGroupToUsersWidgetUser.unknownUser'
              )}
        </span>
        <TimeTrackingConfigurationSelect
          userGroupId={userGroupId}
          value={userGroupToUser.timeTrackingConfigurationId}
          onValueChanged={handleTimeTrackingConfigurationIdChanged}
        ></TimeTrackingConfigurationSelect>
        <Checkbox
          label={t('entities.UserGroup.users.groupAdmin')}
          value={userGroupToUser.groupAdmin}
          onValueChanged={handleGroupAdminChanged}
          data-testid="UserGroupToUsersWidgetUser.GroupAdminCheckbox"
        ></Checkbox>
        <ButtonStandard
          type={ButtonStandardType.Secondary}
          onClick={handleDeleteButtonClick}
          data-testid="UserGroupToUsersWidgetUser.DeleteButton"
        >
          <Icon
            name="trash"
            type={IconType.Light}
          ></Icon>
        </ButtonStandard>
      </div>
    );
  });
